// /app/careers — HR-only careers inbox.
//
// Ported visually from design-handoff/src/pages/secondary.jsx
// CareersPage: a two-pane layout (left = list of applications, right
// = selected application detail). KVKK-consent badge + CV download.
//
// Backend reality (brand-service v0.5.25): only public endpoints exist
// today — GET /v1/public/careers/positions (active job posts) and
// POST /v1/public/careers/applications (candidate submission). There
// is no admin/HR list endpoint yet (see /handlers/careers.go).
//
// So this page renders two sections:
//   1. "Açık pozisyonlar" — live from /v1/public/careers/positions,
//      what candidates currently see on nexusdijital.com/kariyer.
//   2. "Gelen başvurular" — placeholder until the HR admin endpoint
//      (planned: GET /v1/admin/careers/applications) ships. Today
//      every application triggers an email to hr@nexusdijital.com via
//      notif-service, which is HR's actual workflow.

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHead, Empty } from "@/components/primitives";
import { Icons } from "@/components/icons";

export const Route = createFileRoute("/app/careers")({
  component: CareersPage,
});

interface Position {
  slug: string;
  title_tr: string;
  title_en: string;
  location: string;
  work_mode: string;
  summary_tr: string;
  summary_en: string;
  posted_at: string;
  is_active: boolean;
}

interface PositionsResponse {
  positions: Position[];
}

function CareersPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const { data, isLoading } = useQuery({
    queryKey: ["careers", "positions"],
    queryFn: () => api<PositionsResponse>("/public/careers/positions"),
    staleTime: 5 * 60_000,
  });
  const positions = data?.positions ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "11 · Kariyer · İK" : "11 · Careers · HR only"}
        title={tr ? "Gelen başvurular." : "Inbound applications."}
        subtitle={
          tr
            ? "nexusdijital.com/kariyer üzerinden — KVKK onayı zorunlu. Sahip / İK rolleri görüntüleyebilir."
            : "From nexusdijital.com/kariyer — KVKK consent required. Owner / HR roles can view."
        }
        actions={
          <button className="btn btn-secondary btn-sm" disabled title={tr ? "Yakında" : "Coming soon"}>
            <Icons.Download size={13} /> {tr ? "CSV indir" : "Export CSV"}
          </button>
        }
      />

      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "420px 1fr",
          overflow: "hidden",
        }}
      >
        {/* Left: positions list */}
        <div className="scroll-y" style={{ borderRight: "1px solid var(--b1)" }}>
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--b1)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div className="eyebrow" style={{ flex: 1 }}>
              — {tr ? "Açık pozisyonlar" : "Open positions"}
            </div>
            <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
              {positions.length}
            </span>
          </div>

          {isLoading ? (
            <div style={{ padding: 18 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="skel"
                  style={{ height: 64, marginBottom: 10, borderRadius: 8 }}
                />
              ))}
            </div>
          ) : positions.length === 0 ? (
            <div
              style={{
                padding: 32,
                color: "var(--mu)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {tr ? "Aktif pozisyon yok." : "No active positions."}
            </div>
          ) : (
            positions.map((p) => (
              <div
                key={p.slug}
                style={{
                  padding: "14px 18px",
                  borderBottom: "1px solid var(--b1)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                    {tr ? p.title_tr : p.title_en || p.title_tr}
                  </div>
                  {p.is_active ? (
                    <span className="badge badge-green">
                      <span className="dot" />
                      {tr ? "aktif" : "active"}
                    </span>
                  ) : (
                    <span className="badge badge-gray">
                      <span className="dot" />
                      {tr ? "kapalı" : "closed"}
                    </span>
                  )}
                </div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  {p.location} · {p.work_mode} · {tr ? "yayınlandı" : "posted"}{" "}
                  {formatDate(p.posted_at, tr)}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--tx2)",
                    lineHeight: 1.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {tr ? p.summary_tr : p.summary_en || p.summary_tr}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right: applications placeholder */}
        <div
          className="scroll-y"
          style={{
            padding: 32,
            background: "var(--bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Empty
            title={tr ? "Başvuru kutusu yakında" : "Application inbox coming soon"}
            body={
              tr
                ? "Her başvuru şu an hr@nexusdijital.com adresine notif-service üzerinden e-posta olarak iletiliyor — KVKK onay damgası ve 7 gün geçerli CV indirme linki dahil. Yönetici listeleme uç noktası (brand-service /v1/admin/careers/applications) bir sonraki oturumda eklenecek."
                : "Every application currently emails hr@nexusdijital.com via notif-service — including the KVKK consent stamp and a 7-day signed CV download link. The HR list endpoint (brand-service /v1/admin/careers/applications) lands in a later session."
            }
            action={
              <a
                href="https://nexusdijital.com/kariyer"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm"
              >
                <Icons.Globe size={13} /> {tr ? "Kariyer sayfasını aç" : "Open careers page"}
              </a>
            }
          />
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string, tr: boolean): string {
  try {
    return new Date(iso).toLocaleDateString(tr ? "tr-TR" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}
